import { DownloadRounded } from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { IAgGridCustomToolbarActionsParams } from "../../../../../../../util/component/AgGridCustomToolbar";
import CustomLoader from "../../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../../util/state/globalVariables";
import { downloadBookings } from "../../../../../api/services";
import getBookingsCount from "../../../../../api/services/getBookingsCount";
import { BookingScope } from "../../../../../types";
import { createFile, hasScope } from "../../../../../utils";
import { bookingsFilterState } from "../../../recoil";

const ToolbarActions = (params: IAgGridCustomToolbarActionsParams) => {

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);

    const { t } = useTranslation('booking');

    const bookingsFilter = useRecoilValue(bookingsFilterState);

    const downloadBookingsMutation = useMutation(
        downloadBookings,
        {
            onSuccess: (data) => {
                createFile(data.file, data.fileName)
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                })
            }
        }
    );

    const handleDownloadClick = useCallback(async () => {
        const visibleColDefs = (
            params.gridRef?.current?.columnApi
                .getAllDisplayedColumns()
                .map(c => c.getColDef())
                .filter(c => !c.suppressColumnsToolPanel || c.field === 'statusSubstatus') ?? []
        );
        const bookingCount = await getBookingsCount({
            filters: bookingsFilter
        });
        if (bookingCount > 1000) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.download_max_rows'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
            return;
        }
        downloadBookingsMutation.mutate({
            filters: bookingsFilter,
            fields: visibleColDefs.map(cd => ({
                field: cd.field ?? '',
                headerName: cd.headerName ?? ''
            }))
        });
    }, [bookingsFilter]);

    if (!hasScope(globalData, BookingScope.BookingExport)) {
        return <></>;
    }

    return (
        <Box px={2}>
            <Tooltip title={t('label.download')}>
                <IconButton
                    onClick={handleDownloadClick}
                    color="accent"
                >
                    <DownloadRounded />
                </IconButton>
            </Tooltip>
            <CustomLoader loading={downloadBookingsMutation.isLoading} />
        </Box>
    );
};

export default ToolbarActions;
